import React from "react";
import {
  AddressClass,
  AddressId,
  AddressModel,
  HogaDataModel,
  HogaRequestDataTypeId,
  HogaResponseDataId,
  HogaResponseModel
} from "../../../helpers/address";
import ZipCodeField from "./addressField/zipCodeField";
import { ValidatedValue } from "../../../helpers/validator";
import { inject, observer } from "mobx-react";
import AutocompleteInput from "../autocompleteInput/autocompleteInput";
import TextInput from "../textInput/textInput";
import "./address.scss";
import Formatter, { CapitalizationType } from "../../../helpers/formatter";
import { ErrorMessages } from "../../../helpers/errorMessage";
import { HogaService } from "../../../services/services/hogaService";

interface AddressProps {
  address: AddressModel;
  hogaService?: HogaService;
  disabled?: boolean;
  displayLabel?: boolean;
  validateApartmentNumber?: boolean;
}

interface AddressState {
  hogaData: {
    streets: HogaDataModel[];
    cities: HogaDataModel[];
    counties: HogaDataModel[];
  };
  zipCodeFound: boolean;
  lastValidZipCode: string;
}

@inject("hogaService")
@observer
export default class Address extends React.Component<AddressProps, AddressState> {
  constructor(props: AddressProps) {
    super(props);
    this.state = {
      hogaData: {
        streets: [{ Id: "", Text: "" }],
        cities: [{ Id: "", Text: "" }],
        counties: [{ Id: "", Text: "" }]
      },
      zipCodeFound: true,
      lastValidZipCode: this.props.address.zipCode.value
    };
  }

  public componentDidMount() {
    this.initializeHogaData();
  }

  private initializeHogaData() {
    const zipCode = this.props.address.zipCode.value;
    const county = this.props.address.county.value;
    const city = this.props.address.city.value;
    const street = this.props.address.street.value;

    if (zipCode !== "") {
      this.getCountiesFromHoga(zipCode);
    }

    if (zipCode !== "" && county !== "") {
      this.getCitiesFromHoga(zipCode, county);
    }

    if (zipCode !== "" && county !== "" && city !== "") {
      this.getStreetsFromHoga(zipCode, county, city, street);
    }
  }

  public render() {
    return (
      <div className="address">
        {this.renderZipCode()}
        {this.renderCounty()}
        {this.renderCity()}
        {this.renderStreet()}
        {this.renderBuildingNumber()}
        {this.renderApartmentNumber()}
      </div>
    );
  }

  private renderZipCode() {
    return (
      <ZipCodeField
        onBlur={(event) => {
          const value = event.target.value;
          if (this.state.lastValidZipCode !== value) {
            this.cleanAddressForm();
          }
          this.getCountiesFromHoga(value);
          this.setState({ lastValidZipCode: event.target.value });
        }}
        value={this.props.address.zipCode}
        disabled={this.props.disabled}
        displayLabel={this.props.displayLabel}
      />
    );
  }

  private cleanAddressForm() {
    this.props.address.county.setValue("");
    this.props.address.city.setValue("");
    this.props.address.street.setValue("");
  }

  private renderAutocompleteInput(values: string[], fieldName: AddressId, selectedValue: ValidatedValue<string>,
    label: string, getHogaData: (value: any) => void = (value) => { return; }) {
    const filteredValues = this.filterValues(values, selectedValue);
    return (
      <div className={`field-container ${fieldName}`}>
        {this.props.displayLabel && <div className="label">{label}</div>}
        <AutocompleteInput
          values={filteredValues}
          onInputChange={(event, value) => {
            const text = Formatter.capitalizeWords(value, CapitalizationType.full);
            this.onInputChange(fieldName, text);
            selectedValue.setValue(text);
          }}
          clearValidation={() => selectedValue.clearValidation()}
          value={selectedValue.value}
          onBlur={(event) => {
            const value = event.target.value;
            selectedValue.setValue(selectedValue.value.trim());
            selectedValue.validate();
            getHogaData(value);
          }}
          errorMessage={selectedValue.errorMessage}
          topLabel={label}
          disabled={this.props.disabled}
        />
      </div>
    );
  }

  private renderCounty() {
    return this.renderAutocompleteInput(
      this.state.hogaData.counties
        .map((country: HogaDataModel) => Formatter.capitalizeWords(country.Text, CapitalizationType.full)),
      AddressId.county,
      this.props.address.county,
      AddressClass.displayName.county,
      (county) => { this.getCitiesFromHoga(this.props.address.zipCode.value, county); }
    );
  }

  private renderCity() {
    return this.renderAutocompleteInput(
      this.state.hogaData.cities
        .map((city: HogaDataModel) => Formatter.capitalizeWords(city.Text, CapitalizationType.full)),
      AddressId.city,
      this.props.address.city,
      AddressClass.displayName.city,
      (city) => { this.getStreetsFromHoga(this.props.address.zipCode.value, this.props.address.county.value, city, ""); }
    );
  }

  private renderStreet() {
    return this.renderAutocompleteInput(
      this.state.hogaData.streets
        .map((street: HogaDataModel) => Formatter.capitalizeWords(street.Text, CapitalizationType.full)),
      AddressId.street,
      this.props.address.street,
      AddressClass.displayName.street
    );
  }

  private renderBuildingNumber() {
    return (
      <div className="field-container building-number">
        {this.props.displayLabel && <div className="label">{AddressClass.displayName.buildingNumber}</div>}
        <TextInput
          validatedValue={this.props.address.buildingNumber}
          topLabel={AddressClass.displayName.buildingNumber}
          disabled={this.props.disabled}
          className="MuiFormControl-marginNormal"
        />
      </div>
    );
  }

  private renderApartmentNumber() {
    return (
      <div className="field-container apartment-number">
        {this.props.displayLabel && <div className="label">{AddressClass.displayName.apartmentNumber}</div>}
        {<TextInput
          validatedValue={this.props.address.apartmentNumber}
          validationDisabled={!this.props.validateApartmentNumber}
          topLabel={AddressClass.displayName.apartmentNumber}
          disabled={this.props.disabled}
          className="MuiFormControl-marginNormal"
        />}
      </div>
    );
  }

  private getCountiesFromHoga(zipCode: string) {
    const data = {
      zrodloDanychHoga: HogaResponseDataId.counties,
      [HogaRequestDataTypeId.zipCode]: zipCode
    };

    this.props.hogaService!.getDataFromHoga({ Data: data }).then((response: HogaResponseModel) => {
      if (response?.Result) {
        const hogaData = { ...this.state.hogaData, [HogaResponseDataId[HogaResponseDataId.counties]]: response.Result };
        this.setState({ zipCodeFound: hogaData.counties.length !== 0 });
        if (!this.state.zipCodeFound) {
          this.props.address.zipCode.setErrorMessage(ErrorMessages.address.zipCode);
        }
        this.setState({ hogaData });
        this.setDefaultCountyValue(hogaData.counties);
      } else {
        this.setState({ zipCodeFound: true });
      }
    });
  }

  private getCitiesFromHoga(zipCode: string, county: string) {
    const data = {
      zrodloDanychHoga: HogaResponseDataId.cities,
      [HogaRequestDataTypeId.county]: county,
      [HogaRequestDataTypeId.zipCode]: zipCode
    };

    this.props.hogaService!.getDataFromHoga({ Data: data }).then((response: HogaResponseModel) => {
      if (response?.Result) {
        const hogaData = { ...this.state.hogaData, [HogaResponseDataId[HogaResponseDataId.cities]]: response.Result };
        this.setState({ hogaData });
        this.setDefaultCityValue(hogaData.cities);
      } else {
        this.setState({ zipCodeFound: true });
      }
    });
  }

  private getStreetsFromHoga(zipCode: string, county: string, city: string, streetPrefix?: string) {
    const data = {
      zrodloDanychHoga: HogaResponseDataId.streets,
      [HogaRequestDataTypeId.zipCode]: zipCode,
      [HogaRequestDataTypeId.city]: city,
      [HogaRequestDataTypeId.county]: county,
      [HogaRequestDataTypeId.streetPrefix]: streetPrefix,
      [HogaRequestDataTypeId.limit]: 10
    };

    this.props.hogaService!.getDataFromHoga({ Data: data }).then((response: HogaResponseModel) => {
      if (response?.Result) {
        const hogaData = { ...this.state.hogaData, [HogaResponseDataId[HogaResponseDataId.streets]]: response.Result };
        this.setState({ hogaData });
      }
    });
  }

  private setDefaultCountyValue(hogaCountiesData: HogaDataModel[]) {
    if (this.props.address.county.value === "" && hogaCountiesData.length === 1) {
      const county = hogaCountiesData[0].Text;
      this.props.address.county.setValue(county);
      this.getCitiesFromHoga(this.props.address.zipCode.value, county);
      this.props.address.county.validate();
    }
  }

  private setDefaultCityValue(hogaCitiesData: HogaDataModel[]) {
    if (this.props.address.city.value === "" && hogaCitiesData.length === 1) {
      const city = hogaCitiesData[0].Text;
      this.props.address.city.setValue(city);
      this.getStreetsFromHoga(this.props.address.zipCode.value, this.props.address.county.value, city, "");
      this.props.address.city.validate();
    }
  }

  private onInputChange(fieldName: AddressId, value: string) {
    if (fieldName === AddressId.county && value !== this.props.address.county.value) {
      this.props.address.city.setValue("");
      this.props.address.street.setValue("");
    }
    if (fieldName === AddressId.city && value !== this.props.address.city.value) {
      this.props.address.street.setValue("");
    }
    if (fieldName === AddressId.street) {
      this.getStreetsFromHoga(this.props.address.zipCode.value, this.props.address.county.value, this.props.address.city.value, value);
    }
  }

  private filterValues(values: string[], selectedValue: ValidatedValue<string>) {
    return values.filter(value => value.includes(selectedValue.value)).sort((a, b) => a.localeCompare(b, "pl"));
  }
}
